import { Injectable } from '@angular/core';

import Pusher from 'pusher-js';
import * as PusherTypes from 'pusher-js';

import { BehaviorSubject, combineLatest, filter, first, map, ReplaySubject, Subject } from 'rxjs';
import { IPusherEvent, IPusherMessage, PusherChannels, PusherEvent } from '../interfaces/pusher.interfaces';
import { AppLog } from '../util/log.util';
import { environment } from '../../../environments/environment';
import { CoreService } from './core.service';

@Injectable({
  providedIn: 'root'
})
export class PusherService {

  alive: boolean = false;

  pusher: any;

  systemChannel: any;
  private systemChannelSubscription: Subject<boolean> = new Subject();

  privateChannel: any;
  private privateChannelSubscription: Subject<boolean> = new Subject();

  nyomdaChannel: any = false;
  private nyomdaChannelSubscription: Subject<boolean> = new Subject();

  user: number;

  private subscriptions$ = combineLatest([
    this.systemChannelSubscription.pipe(filter(x => x === true)),
    this.privateChannelSubscription.pipe(filter(x => x === true)),
    this.nyomdaChannelSubscription.pipe(filter(x => x === true)),
  ]).pipe(
    map(([systemState, privateState, nyomdaState]) => !!systemState && !!privateState && !!nyomdaState)
  );

  channelSubscriptionsState: ReplaySubject<boolean> = new ReplaySubject(1);

  private pusherEvents: BehaviorSubject<IPusherEvent[]> = new BehaviorSubject([]);

  public events = this.pusherEvents.asObservable();

  // TODO pre-koncepció: apinként tárolnánk az event üzeneteket?? esetleg mondjuk lehetne event típusonként is
  // public messageApiSubject: BehaviorSubject<IPusherMessage[]> = new BehaviorSubject([]);
  
  constructor(private coreService: CoreService) { 

    /*
    try {
      
      this.pusher = new Pusher(
        environment.pusher.key,
        {
          cluster: environment.pusher.cluster
        }
      );

      this.alive = true;

    } catch (e) {
      AppLog.log('Pusher connection failed because unknown error occured', [e.message]);
    } 
    */

  }

  /**
   * Kényelmi metódus amit a rendszer channel nevének lekérdezéséhez tudunk használni
   */
  get system() {
    return 'system-channel';
  }

  /**
   * Kényelmi metódus amit a privát felhasználói channel nevének lekérdezéséhez tudunk használni
   */
  get private() {
    return `private-user-${this.user}`;
  }

  get nyomda() {
    return `nyomda-channel-${this.coreService.getNyomdaId()}`;
  }

  initService(userId: number): void {

    try {

      // tároljuk az aktuális felhasználó id-t
      this.user = userId;
   
      // konzol logolás, ha szükséges (env-ből kapcsolható)
      if (environment.debugOptions.pusher)
        Pusher.logToConsole = true;

      // összerakjuk a kapcsolatot, az authorizációt is a saját végpontunk végzi
      // auth* tulajdonságok workaround miatt van, különben nem építené be a kérésbe a Bearer tokenünket
      this.pusher = new Pusher(
        environment.pusher.key,
        {
          cluster: environment.pusher.cluster,
          authEndpoint: environment.endpoints.app + '/pusher',          
          authTransport: 'ajax',
          auth: {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(environment.storage.token)}`
            }
          },
          userAuthentication: {
            endpoint: environment.endpoints.app + '/pusher',
            transport: 'ajax',
            headersProvider: () => {
              return {
                Authorization: `Bearer ${localStorage.getItem(environment.storage.token)}`
              };
            }
          },
          forceTLS: true
        }
      );

      // bindelünk a connected, illetve az error eventre (átadjuk a this-t is, mert kellhet nekünk a callbackban)    
      this.pusher.connection.bind("connected", this.connectedExecute.bind(this));
      this.pusher.connection.bind("error", this.handleErrors.bind(this)); 

      this.subscriptions$.pipe(first()).subscribe({ next: (value: boolean) => {
        this.channelSubscriptionsState.next(value);
      }})

    } catch(e) {
      AppLog.log('Pusher error', [e.message]);
    }
    
  }

  /**
   * Privát metódusunk a hibakezelésre, ha csak lehet akkor itt próbáljunk meg minden hibát központilag kezelni
   * 
   * @param error 
   */
  private handleErrors(error): void {

    if (error.type === 'PusherError') {
  
      // TODO https://docs.bird.com/pusher/channels/channels/troubleshooting/why-am-i-receiving-the-invalid-signature-expected-hmac-sha256-error
      if (error.data.message.includes("Invalid signature"))
        AppLog.message('Silented invalid HMAC signature error');

    }

  }

  /**
   * Minden központi bindelésünket itt kezeljük, mivel az app szinten "perzisztált" 
   * behavior subjecteket is itt kezeljük.
   */
  connectedExecute(): void {
    
    this.systemChannel = this.pusher.subscribe("system-channel");
    this.systemChannel.bind("pusher:subscription_succeeded", (r) => {
      AppLog.message('Successfully subscribed to system channel');
      this.systemChannelSubscription.next(true);      
    });

    this.privateChannel = this.pusher.subscribe(`private-user-${this.user}`); 
    this.privateChannel.bind("pusher:subscription_succeeded", (r) => {
      AppLog.message('Successfully subscribed to user channel');
      this.privateChannelSubscription.next(true);
    });
    
    this.nyomdaChannel = this.pusher.subscribe(`nyomda-channel-${this.coreService.getNyomdaId()}`); 
    this.nyomdaChannel.bind("pusher:subscription_succeeded", (r) => {
      AppLog.message('Successfully subscribed to nyomda channel');
      this.nyomdaChannelSubscription.next(true);
    });
    
    if (environment.debug) {

      console.log('[DEBUG][Pusher] Instance connected');

      this.pusher.allChannels().forEach(channel => {
        console.log('[DEBUG][Pusher] Channel subscription: ' + channel.name);
      });

    }

  }

  subscribeUserEvent(event: string, callback: (data: unknown, parent: unknown) => void, parent: unknown): void {        
    this.privateChannel.bind(event, data => callback(data, parent));
  }

  subscribeNyomdaEvent(event: string, callback: (data: unknown, parent: unknown) => void, parent: unknown): void {        
    this.nyomdaChannel.bind(event, data => callback(data, parent));
  }  

  /**
   * Minden központi bindelésünket itt kezeljük, mivel az app szinten "perzisztált" 
   * behavior subjecteket is itt kezeljük.
   */
  bindMessageAPI(): void {

      // message api általános bind, amennyiben találunk ilyen channelt
      if (this.pusher.allChannels().find(c => c.name === PusherChannels.MESSAGE_API)) {

        const channel = this.pusher.allChannels().find(c => c.name === PusherChannels.MESSAGE_API); 
        channel.bind('general', (data: IPusherMessage) => {      
          
          let eventList = this.pusherEvents.value as unknown[] as IPusherEvent[];  
          eventList.push(
            (new PusherEvent)
              .setChannel(PusherChannels.MESSAGE_API)
              .setDateTime(data.datetime)
              .setMessage(data)
          );

          this.pusherEvents.next(eventList);                 
        });
  
      }

  }

  /**
   * Minden központi bindelésünket itt kezeljük, mivel az app szinten "perzisztált" 
   * behavior subjecteket is itt kezeljük.
   */
  bindCallasAPI(): void {

      // message api általános bind, amennyiben találunk ilyen channelt
      if (this.pusher.allChannels().find(c => c.name === PusherChannels.CALLAS_API)) {

        const channel = this.pusher.allChannels().find(c => c.name === PusherChannels.CALLAS_API); 
        channel.bind('general', (data: IPusherMessage) => {      
          
          let eventList = this.pusherEvents.value as unknown[] as IPusherEvent[];  
          eventList.push(
            (new PusherEvent)
              .setChannel(PusherChannels.CALLAS_API)
              .setDateTime(data.datetime)
              .setMessage(data)
          );

          this.pusherEvents.next(eventList);                 
        });
  
      }
      
  }

  /**
   * Kényelmi funkció, ami visszaadja nekünk az argumentumban kapott channel esemény listáját
   * 
   * @param channel 
   * @returns 
   */
  getChannelEvents(channel: string): IPusherEvent[] {
    return this.pusherEvents.value.filter(c => c.channel === channel);
  }

  /**
   * Kényelmi funkció, ami visszaadja nekünk a teljes behavior subject tartalmat
   * 
   * @returns 
   */
  getCurrentEvents(): IPusherEvent[] {
    return this.pusherEvents.value;
  }

  getEvents(): BehaviorSubject<IPusherEvent[]> {
    return this.pusherEvents;
  }

  /**
   * Egy adott channel nevet ellenőriz a feliratkozások között
   * 
   * @param channel 
   * @returns 
   */
  private isChannelSubscribed(channel: string): boolean {
    
    let a = false;
    this.pusher.allChannels().forEach(c => {
      if (c.name === channel) a = true;
    });

    return a;
  }

}
